import React, { useState, useEffect } from "react";
import axios from "axios";
import { Copy, Check, Crosshair, ChevronDown, ChevronUp, RotateCcw, Pencil, Link2, Loader2 } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { toast } from "sonner";
import { InjectLinkButton } from "./InjectLink";

// All persisted state is namespaced to the current user_id so signing out and
// signing back in as a different account NEVER inherits the previous user's
// offer URL, UTM template, or per-video overrides. This is a security boundary
// (one user must not see another user's landing URL) and a UX must (different
// users obviously want different settings).
const STORE_BASE = "utm_builder_settings_v1";
const OVERRIDES_BASE = "utm_overrides_v1";

function _userKey(base, userId) {
  return userId ? `${base}:${userId}` : base;
}

const DEFAULTS = {
  baseUrl: "https://yourwebsite.com",
  utmSource: "youtube",
  utmMedium: "video",
  // utm_content is the standard UTM slot for distinguishing the SAME base URL
  // across variants — perfect for a human-readable video identifier so you can
  // tell which video produced each click in GA / your own DB. Supports the
  // tokens `{video_title}` (slugified) and `{video_id}` (raw YouTube id).
  utmContent: "{video_title}",
};

export function useUtmSettings(userId) {
  const key = _userKey(STORE_BASE, userId);
  const [settings, setSettings] = useState(() => {
    if (!userId) return DEFAULTS; // never read until user is known
    try {
      const raw = localStorage.getItem(key);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch {
      return DEFAULTS;
    }
  });

  // Reload from storage when the userId changes (sign-out → sign-in with a
  // different account in the same browser session). Without this, the React
  // state holds the previous user's settings.
  useEffect(() => {
    if (!userId) { setSettings(DEFAULTS); return; }
    try {
      const raw = localStorage.getItem(key);
      setSettings(raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS);
    } catch {
      setSettings(DEFAULTS);
    }
  }, [userId, key]);

  useEffect(() => {
    if (!userId) return;
    try { localStorage.setItem(key, JSON.stringify(settings)); }
    catch (err) { console.warn("localStorage disabled — UTM settings won't persist:", err?.message || err); }
  }, [settings, key, userId]);

  return [settings, setSettings];
}

function normalizeBase(url) {
  let u = (url || "").trim().replace(/\/+$/, "");
  if (!u) return DEFAULTS.baseUrl;
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;
  return u;
}

function slugify(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")     // strip accents
    .replace(/['’"`]/g, "")              // drop quote marks before hyphenating
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function renderUtmContent(template, videoId, videoTitle) {
  const tpl = (template || "").trim();
  if (!tpl) return "";
  const titleSlug = slugify(videoTitle);
  return tpl
    .replace(/\{video_title\}/gi, titleSlug)
    .replace(/\{video_id\}/gi, videoId || "");
}

export function buildUtmLink(settings, videoId, videoTitle = "", contentOverride = null) {
  const base = normalizeBase(settings.baseUrl);
  const src = encodeURIComponent(settings.utmSource || DEFAULTS.utmSource);
  const med = encodeURIComponent(settings.utmMedium || DEFAULTS.utmMedium);
  // Per-video override (literal value, no token rendering). null/undefined → fall back to template.
  const content = contentOverride != null
    ? contentOverride.trim()
    : renderUtmContent(settings.utmContent ?? DEFAULTS.utmContent, videoId, videoTitle);
  let url = `${base}?utm_source=${src}&utm_campaign=${encodeURIComponent(videoId)}&utm_medium=${med}`;
  if (content) url += `&utm_content=${encodeURIComponent(content)}`;
  return url;
}

// ----- Per-video UTM overrides ---------------------------------------------
// localStorage map: { [video_id]: utm_content_value }, scoped per user_id.

export function useUtmOverrides(userId) {
  const key = _userKey(OVERRIDES_BASE, userId);
  const [overrides, setOverrides] = useState(() => {
    if (!userId) return {};
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  // Reload on user change — see comment in useUtmSettings above.
  useEffect(() => {
    if (!userId) { setOverrides({}); return; }
    try {
      const raw = localStorage.getItem(key);
      setOverrides(raw ? JSON.parse(raw) : {});
    } catch {
      setOverrides({});
    }
  }, [userId, key]);
  useEffect(() => {
    if (!userId) return;
    try { localStorage.setItem(key, JSON.stringify(overrides)); }
    catch (err) { console.warn("localStorage disabled — UTM overrides won't persist:", err?.message || err); }
  }, [overrides, key, userId]);
  const setOverride = (videoId, value) =>
    setOverrides((m) => {
      const next = { ...m };
      if (value == null || value === "") delete next[videoId];
      else next[videoId] = value;
      return next;
    });
  return [overrides, setOverride];
}

export { renderUtmContent };

export function UtmBuilder({ settings, onChange }) {
  const [open, setOpen] = useState(true);
  const [local, setLocal] = useState(settings);
  const [savedFlash, setSavedFlash] = useState(false);

  // Sync external -> local
  useEffect(() => { setLocal(settings); }, [settings]);

  const save = () => {
    onChange({
      baseUrl: normalizeBase(local.baseUrl),
      utmSource: (local.utmSource || DEFAULTS.utmSource).trim(),
      utmMedium: (local.utmMedium || DEFAULTS.utmMedium).trim(),
      utmContent: (local.utmContent ?? DEFAULTS.utmContent).trim(),
    });
    setSavedFlash(true);
    toast.success("UTM defaults updated");
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const reset = () => {
    setLocal(DEFAULTS);
    onChange(DEFAULTS);
    toast.success("Reset to defaults");
  };

  const dirty =
    local.baseUrl !== settings.baseUrl ||
    local.utmSource !== settings.utmSource ||
    local.utmMedium !== settings.utmMedium ||
    (local.utmContent ?? "") !== (settings.utmContent ?? "");

  // Live example using a placeholder video id + title so the preview shows the
  // utm_content slug the same way the table rows will render it.
  const example = buildUtmLink(local, "VIDEO_ID", "My Video Title");

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl animate-fade-up"
      data-testid="utm-builder"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 sm:px-8 py-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid="utm-builder-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-red-500/10 text-red-400 border border-red-500/25">
            <Crosshair className="w-4 h-4" />
          </div>
          <div>
            <div className="text-white font-semibold tracking-tight">UTM Builder</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono truncate max-w-[60ch]">
              {example}
            </div>
          </div>
        </div>
        <div className="text-zinc-500 ml-3 flex-shrink-0">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Base URL
            </Label>
            <Input
              value={local.baseUrl}
              onChange={(e) => setLocal({ ...local, baseUrl: e.target.value })}
              placeholder="https://yoursite.com"
              className="mt-1.5 bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="utm-base-url-input"
            />
            <p className="text-xs text-zinc-600 mt-1.5">Your store / landing page. We'll add the UTM params.</p>
          </div>
          <div className="md:col-span-3">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              utm_source
            </Label>
            <Input
              value={local.utmSource}
              onChange={(e) => setLocal({ ...local, utmSource: e.target.value })}
              placeholder="youtube"
              className="mt-1.5 bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="utm-source-input"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              utm_medium
            </Label>
            <Input
              value={local.utmMedium}
              onChange={(e) => setLocal({ ...local, utmMedium: e.target.value })}
              placeholder="video"
              className="mt-1.5 bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="utm-medium-input"
            />
          </div>

          <div className="md:col-span-9">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold flex items-center gap-2">
              utm_content
              <span className="text-zinc-600 normal-case tracking-normal font-normal text-[10px]">— human-readable video tag</span>
            </Label>
            <Input
              value={local.utmContent ?? ""}
              onChange={(e) => setLocal({ ...local, utmContent: e.target.value })}
              placeholder="{video_title}"
              className="mt-1.5 bg-black/40 border-white/10 text-white font-mono text-sm"
              data-testid="utm-content-input"
            />
            <p className="text-xs text-zinc-600 mt-1.5">
              Tokens: <code className="text-red-300">{`{video_title}`}</code> (auto-slugified), <code className="text-red-300">{`{video_id}`}</code>. Leave blank to omit the field. Examples: <code className="text-zinc-400">{`{video_title}`}</code>, <code className="text-zinc-400">{`{video_id}-{video_title}`}</code>, <code className="text-zinc-400">launch-promo</code>.
            </p>
          </div>
          <div className="md:col-span-3 flex md:items-end gap-2">
            <Button
              onClick={save}
              disabled={!dirty}
              className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_15px_rgba(255,0,0,0.3)] disabled:opacity-40 disabled:shadow-none flex-1"
              data-testid="utm-save-btn"
            >
              {savedFlash ? <><Check className="w-4 h-4 mr-1.5" /> Saved</> : "Save"}
            </Button>
            <Button
              onClick={reset}
              variant="ghost"
              className="text-zinc-500 hover:text-white hover:bg-white/5"
              data-testid="utm-reset-btn"
              title="Reset to defaults"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          <div className="md:col-span-12">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Preview
            </Label>
            <div className="mt-1.5 px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 font-mono text-xs text-red-300 break-all" data-testid="utm-preview">
              {example}
            </div>
            <p className="text-xs text-zinc-600 mt-1.5">
              <span className="text-zinc-400">utm_campaign</span> stays the YouTube video id — that's how the pixel attributes sales back to the right video. <span className="text-zinc-400">utm_content</span> is the human-readable tag that lets you tell which video produced each visit in GA / your own DB.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// InlineUtmCell — the per-video cell rendered in the videos table.
// Carries: the live UTM link preview, copy button, per-video override (pencil),
// and a Copy-short-link button that hits the backend to mint a pretty URL.
// =============================================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function InlineUtmCell({
  link,
  testidPrefix,
  // Optional — passed by the parent so the cell can edit its own override + mint short links
  videoId,
  videoTitle = "",
  utmSettings,
  override,
  onOverrideChange,
  // For the per-row "Inject link" button — fetched once at the dashboard level
  // and passed in so we don't burn N status calls per render.
  ytStatus,
  onReauthorize,
}) {
  const [copied, setCopied] = useState(false);
  const [shortCopied, setShortCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(override ?? "");
  const [shortLoading, setShortLoading] = useState(false);
  const [shortUrl, setShortUrl] = useState(null);

  // Keep local draft in sync when the parent's override prop changes
  useEffect(() => { setDraft(override ?? ""); }, [override]);

  // Effective default — what the template would produce — shown as the input placeholder
  const templateDefault = videoId && utmSettings
    ? renderUtmContent(utmSettings.utmContent ?? "", videoId, videoTitle)
    : "";

  const copy = async (e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("UTM link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyShortLink = async (e) => {
    e?.stopPropagation();
    if (!videoId || !utmSettings) return;
    setShortLoading(true);
    try {
      const { data } = await axios.post(`${API}/links/short`, {
        video_id: videoId,
        video_title: videoTitle,
        base_url: utmSettings.baseUrl,
        utm_source: utmSettings.utmSource,
        utm_medium: utmSettings.utmMedium,
        utm_content: override != null
          ? override
          : renderUtmContent(utmSettings.utmContent ?? "", videoId, videoTitle),
      });
      setShortUrl(data.short_url);
      try {
        await navigator.clipboard.writeText(data.short_url);
        setShortCopied(true);
        toast.success("Pretty short link copied");
        setTimeout(() => setShortCopied(false), 1800);
      } catch {
        toast.info("Short link minted — clipboard blocked, click the link to copy manually");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mint short link");
    } finally {
      setShortLoading(false);
    }
  };

  const saveOverride = () => {
    const v = draft.trim();
    onOverrideChange?.(videoId, v || null);
    setEditing(false);
    if (v) toast.success("Per-video UTM saved");
    else toast.success("Override cleared — using template");
  };

  const showEditor = !!onOverrideChange && !!videoId;
  const showShort = !!utmSettings && !!videoId;

  return (
    <div className="min-w-0 flex flex-col gap-1.5" data-testid={testidPrefix}>
      {/* Row 1 — link preview + copy + pencil + short */}
      <div className="flex items-center gap-1.5 min-w-0">
        <div
          className="font-mono text-xs text-zinc-400 bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 truncate flex-1 min-w-0"
          title={link}
        >
          {link}
        </div>
        <Button
          size="sm"
          onClick={copy}
          className={`flex-shrink-0 h-8 px-2.5 ${copied
            ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/15 border border-emerald-500/30"
            : "bg-white/5 hover:bg-red-500/15 text-zinc-300 hover:text-red-300 border border-white/10 hover:border-red-500/30"
          }`}
          data-testid={`${testidPrefix}-copy`}
          title="Copy full UTM link"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
        {showShort && (
          <Button
            size="sm"
            onClick={copyShortLink}
            disabled={shortLoading}
            className={`flex-shrink-0 h-8 px-2.5 ${shortCopied
              ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/15 border border-emerald-500/30"
              : "bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/25"
            }`}
            data-testid={`${testidPrefix}-short`}
            title="Mint & copy pretty short link"
          >
            {shortLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : shortCopied ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Link2 className="w-3.5 h-3.5" />
            )}
          </Button>
        )}
        {showShort && videoId && (
          <InjectLinkButton
            videoId={videoId}
            videoTitle={videoTitle}
            ytStatus={ytStatus}
            onReauthorize={onReauthorize}
            testid={`${testidPrefix}-inject`}
          />
        )}
        {showEditor && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing((v) => !v)}
            className={`flex-shrink-0 h-8 px-2 ${override
              ? "text-red-400 hover:text-red-300 hover:bg-red-500/10"
              : "text-zinc-500 hover:text-white hover:bg-white/5"
            }`}
            data-testid={`${testidPrefix}-edit`}
            title={override ? `Override: utm_content=${override}` : "Override utm_content for this video"}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Row 2 — short link readout (when minted) */}
      {shortUrl && (
        <a
          href={shortUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-zinc-500 hover:text-red-400 underline underline-offset-2 truncate"
          title={shortUrl}
          data-testid={`${testidPrefix}-short-url`}
        >
          {shortUrl.replace(/^https?:\/\//, "")}
        </a>
      )}

      {/* Row 3 — inline override editor */}
      {editing && showEditor && (
        <div className="flex items-center gap-1.5 mt-1" data-testid={`${testidPrefix}-edit-row`}>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={templateDefault || "{video_title}"}
            className="h-8 bg-black/40 border-white/10 text-white font-mono text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") saveOverride();
              if (e.key === "Escape") { setDraft(override ?? ""); setEditing(false); }
            }}
            data-testid={`${testidPrefix}-edit-input`}
          />
          <Button
            size="sm"
            onClick={saveOverride}
            className="h-8 px-2.5 bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
            data-testid={`${testidPrefix}-edit-save`}
          >
            <Check className="w-3.5 h-3.5" />
          </Button>
          {override && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { onOverrideChange?.(videoId, null); setEditing(false); toast.success("Override cleared"); }}
              className="h-8 px-2 text-zinc-500 hover:text-white hover:bg-white/5"
              data-testid={`${testidPrefix}-edit-reset`}
              title="Use template"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
